import { currentLang } from './i18n';
import {
  currentSearchQuery, setCurrentSearchQuery,
  currentDeadlineFilter, setCurrentDeadlineFilter,
  currentStatusFilter, setCurrentStatusFilter,
  currentMainTab,
  webExpandedFilterItems,
  cachedAllGoals,
  DONE_GOALS_PAGE_SIZE, setDoneGoalsLimit,
  doneGoalsHasMore,
} from './state';
import { goalDeadlineDate, itemMatchesDeadlineFilter } from './helpers';
import { renderAll, renderToday, updateFilterCounts } from './goals';
import type { GoalWithSubtasks } from './types';
import { customersMatching, goalCustomers, onGoalCustomersRead } from './goal-groupings';
import type { FilterCountMap, SearchResultRenderModel } from './components/shared';

let isSearchOpen = false;

function renderSearchBox() {
  const host = document.getElementById('headerSearchBoxHost') as (HTMLElement & { active?: boolean; query?: string }) | null;
  if (!host) return;
  host.active = isSearchOpen;
  host.query = currentSearchQuery;
}

export function renderFilterBar(counts?: FilterCountMap) {
  const host = document.getElementById('allFilterBar') as (HTMLElement & {
    deadlineFilter?: string;
    statusFilter?: string;
    counts?: FilterCountMap;
    doneHasMore?: boolean;
  }) | null;
  if (!host) return;
  host.deadlineFilter = currentDeadlineFilter;
  host.statusFilter = currentStatusFilter;
  host.doneHasMore = doneGoalsHasMore;
  if (counts) host.counts = counts;
}

export function toggleFilterExpand(goalId: string, sectionKey: string, btn: HTMLElement) {
  const key = goalId + ':' + sectionKey;
  if (webExpandedFilterItems.has(key)) {
    webExpandedFilterItems.delete(key);
  } else {
    webExpandedFilterItems.add(key);
  }
  renderToday();
  renderAll();
  renderSearch();
}

export function filterGoalsByDeadline(goals: GoalWithSubtasks[], filter: string) {
  if (filter === 'all') return goals;

  return goals.filter(g => {
    const activeSubDeadlines = (g.subtasks || []).filter(st => st.status !== 'done' && st.status !== 'invalidated').map(st => st.deadline);
    const goalDates = [g.start_at, goalDeadlineDate(g), ...activeSubDeadlines];
    if (filter === 'none') return goalDates.every(d => !d);
    return goalDates.some(d => itemMatchesDeadlineFilter(d, filter));
  });
}

export function setDeadlineFilter(filter: string, btnEl: Element) {
  setCurrentDeadlineFilter(filter);
  void btnEl;
  renderGoalFilters();
}

export function setStatusFilter(filter: string, btnEl: Element) {
  setCurrentStatusFilter(filter);
  void btnEl;
  if (filter === 'done') {
    setDoneGoalsLimit(DONE_GOALS_PAGE_SIZE);
    void import('./upload').then(({ loadAllGoals }) => loadAllGoals());
  }
  renderGoalFilters();
}

function renderGoalFilters() {
  renderToday();
  renderAll();
  updateFilterCounts();
}

export function goalMatchesSearch(g: GoalWithSubtasks) {
  if (!currentSearchQuery) return true;
  const q = currentSearchQuery;
  if (g.title.toLowerCase().includes(q)) return true;
  if ((g.subtasks || []).some(st => st.title.toLowerCase().includes(q))) return true;
  if ((g.decisions || []).some(st => st.title.toLowerCase().includes(q))) return true;
  // #3805: the customer domain (inferred) finds that customer's goals too.
  if (goalCustomers(g.id).some(({ domain }) => domain.includes(q))) return true;
  return false;
}

export function toggleSearch() {
  const actions = document.getElementById('userInfo');
  actions.classList.add('searching');
  document.querySelector('.main-tabs').classList.add('searching');
  document.querySelectorAll('.panel').forEach(el => el.classList.remove('active'));
  document.getElementById('panelSearch').classList.add('active');
  isSearchOpen = true;
  renderSearchBox();
  renderSearch();
}

export function setSearchQuery(q: string) {
  setCurrentSearchQuery(q.trim().toLowerCase());
  renderSearchBox();
  renderSearch();
}

export function clearSearch() {
  setCurrentSearchQuery('');
  isSearchOpen = false;
  renderSearchBox();
  document.getElementById('userInfo').classList.remove('searching');
  document.querySelector('.main-tabs').classList.remove('searching');
  document.querySelectorAll('.panel').forEach(el => el.classList.remove('active'));
  // The same panels as switchMainTab: 今日 and すべて are the list, 完了 the goal cards.
  const panelId = { today: 'panelToday', all: 'panelToday', done: 'panelAll', create: 'panelCreate' }[currentMainTab] || 'panelToday';
  document.getElementById(panelId).classList.add('active');
}

export function renderSearch() {
  void currentLang;
  const host = document.getElementById('searchPanel') as (HTMLElement & { model?: SearchResultRenderModel }) | null;
  if (!host) return;
  const results = currentSearchQuery ? cachedAllGoals.filter(goalMatchesSearch) : [];
  host.model = {
    query: currentSearchQuery,
    resultCount: results.length,
    goals: results,
    customers: currentSearchQuery ? customersMatching(currentSearchQuery) : [],
  };
  renderSearchBox();
}

onGoalCustomersRead(() => {
  if (currentSearchQuery) renderSearch();
});

export function toggleUserMenu() {
  document.getElementById('userDropdown').classList.toggle('open');
}

export function closeUserMenu() {
  document.getElementById('userDropdown').classList.remove('open');
}

// Close dropdown on outside click
document.addEventListener('click', (e) => {
  const wrap = document.querySelector('.user-menu-wrap');
  if (wrap && !wrap.contains(e.target as Node)) closeUserMenu();
});
